import useSWR from 'swr';
import { useRouter } from 'next/router';
import { route } from 'nextjs-routes';
import FrontendHttpClient from '@/utils/http-handler/frontend-http-client';

/**
 * Loads the certificate template's version history (newest first), with each
 * entry populated with the user who triggered the save. Returns null while
 * loading and an empty array when the template has no history yet.
 *
 * @typedef {object} VersionHistoryEntry
 * @property {string} updatedAt ISO timestamp
 * @property {string} filePath S3 path of the template PDF at that point in time
 * @property {'create' | 'edit'} action
 * @property {string} userId
 * @property {null | {
 *   id: string,
 *   email: string,
 *   firstName: string,
 *   lastName: string,
 * }} user
 *
 * @returns {{
 *   history: VersionHistoryEntry[] | null,
 *   isLoading: boolean,
 *   error: unknown,
 *   mutate: () => Promise<unknown>,
 * }}
 */
export const useTemplateVersionHistory = () => {
  const router = useRouter();
  const { userTemplateId } = router.query;
  const isRenewal = router.pathname === '/renew/edit-cert-template';

  const apiRoute = isRenewal
    ? '/api/v2/renewal_cert_templates/[id]/version-history'
    : '/api/v2/certificate_templates/[id]/version-history';

  const { data, error, isLoading, mutate } = useSWR(
    userTemplateId
      ? route({ pathname: apiRoute, query: { id: userTemplateId } })
      : null,
    async url => {
      const res = await FrontendHttpClient.get(url);
      return res.data;
    }
  );

  return {
    history: data?.history ?? null,
    isLoading,
    error,
    mutate,
  };
};
